import feelsLogo from '../../Assets/career_logos/feels_logo.jpeg';
import kineticCommerceLogo from '../../Assets/career_logos/kinetic_commerce_logo.jpg';
import rbcLogo from '../../Assets/career_logos/rbc_wealth_management_logo.jpeg';
import snapLogo from '../../Assets/career_logos/snap_inc_co_logo.jpeg';
import storyStarLogo from '../../Assets/career_logos/story_star_logo.jpeg';
import turboRocketGamesLogo from '../../Assets/career_logos/turbo_rocket_games_logo.jpg';
import brockLogo from '../../Assets/education_logos/brock_university_logo.jpeg';
import feelsAppDiscovery from '../../Assets/portfolio/feels-app-discovery.jpg';
import feelsAppMessaging from '../../Assets/portfolio/feels-app-messaging.jpg';
import snapVoiceOver from '../../Assets/portfolio/snap-voice-over.jpg';
import snapAutoCaption from '../../Assets/portfolio/snap-auto-caption.jpg';
import snapFilterCarousel from '../../Assets/portfolio/snap-filter-carousel.jpg';
import storyStarSpotlight from '../../Assets/portfolio/story-star-spotlight.jpg';
import storyStarPeek from '../../Assets/portfolio/story-star-products/peek.jpg';
import storyStarEllie from '../../Assets/portfolio/story-star-products/ellie.jpg';
import storyStarHeadswitch from '../../Assets/portfolio/story-star-products/headswitch.jpg';
import storyStarSwitchcam from '../../Assets/portfolio/story-star-products/switchcam.jpg';
import storyStarMustknow from '../../Assets/portfolio/story-star-products/mustknow.jpg';
import storyStarKingsCup from '../../Assets/portfolio/story-star-products/kings-cup.jpg';
import storyStarAdventCalendar from '../../Assets/portfolio/story-star-products/advent-calendar.jpg';
import storyStarOhMyGod from '../../Assets/portfolio/story-star-products/oh-my-god.jpg';
import storyStarSponge from '../../Assets/portfolio/story-star-products/sponge.jpg';
import storyStarVibes from '../../Assets/portfolio/story-star-products/vibes.jpg';
import rbcMobileDashboard from '../../Assets/portfolio/rbc-mobile-dashboard.png';
import rbcMobileAccounts from '../../Assets/portfolio/rbc-mobile-accounts.png';
import kineticCommerceCover from '../../Assets/portfolio/kinetic-commerce-cover.jpg';
import dragonSimWorld from '../../Assets/portfolio/dragon-sim-world.jpg';
import dragonSimGameplay from '../../Assets/portfolio/dragon-sim-gameplay.jpg';

export const EXPERIENCE_ORDER = [
    'feels',
    'snap',
    'story-star',
    'rbc-wealth-management',
    'kinetic-commerce',
    'turbo-rocket-games',
    'eventlime',
    'brock-university',
    'agency-sm-art',
];

export const EXPERIENCES = {
    feels: {
        slug: 'feels',
        company: 'Feels Music Messaging',
        role: 'Senior Software Engineer and Advisor',
        dates: 'Oct 2022 to Oct 2023',
        logo: feelsLogo,
        domain: 'feelsmusic.com',
        headline: 'A cross-platform music product and the artist tools behind it.',
        summary: 'I owned hands-on delivery across the native iOS consumer app and a React and Node.js artist platform, while coordinating Android so the product, APIs, and releases stayed aligned across every surface.',
        metrics: [
            { value: '3', label: 'coordinated product surfaces' },
            { value: 'iOS + web', label: 'built hands-on' },
            { value: 'In-house', label: 'engineering transition' },
        ],
        overview: [
            {
                title: 'The product',
                body: 'Feels treated music as a way to communicate, not simply as content to consume. The consumer experience connected native mobile interaction with the systems artists used to manage what appeared on the platform.',
            },
            {
                title: 'The systems underneath it',
                body: 'A native iOS client, a React and Node.js artist console, content-management workflows, media and artwork tooling, engagement analytics, audio recognition, search, and synchronization all had to behave like one product.',
            },
        ],
        media: [
            {
                image: feelsAppDiscovery,
                alt: 'Feels Music Messaging shown inside an iMessage conversation',
                caption: 'The current public App Store presentation of Feels as a music-first messaging experience.',
                href: 'https://apps.apple.com/us/app/feels-music-messaging/id1641397528',
                sourceLabel: 'View on the App Store',
            },
            {
                image: feelsAppMessaging,
                alt: 'Feels Music Messaging product screen from the App Store',
                caption: 'A public product view of the consumer experience. The listing reflects the product after my 2022 to 2023 tenure.',
                href: 'https://apps.apple.com/us/app/feels-music-messaging/id1641397528',
                sourceLabel: 'View on the App Store',
            },
        ],
        sections: [
            {
                number: '01',
                title: 'Built across the consumer and artist sides',
                body: 'I built the native iOS consumer app and the React and Node.js artist platform hands-on. The artist console let creators manage their media, artwork, platform presence, content workflows, and engagement metrics for individual Feels.',
            },
            {
                number: '02',
                title: 'Connected content, audio, and discovery',
                body: 'I built ShazamKit audio recognition and prototyped machine-learning-assisted search and media synchronization. The work connected media ingestion, metadata, discovery, and the user-facing experience rather than treating them as separate features.',
            },
            {
                number: '03',
                title: 'Moved delivery from vendor to team',
                body: 'I coordinated the external Android team to keep features, APIs, releases, and product behavior aligned with iOS and web. I helped move engineering in-house, hired engineers into the team, and mentored engineering interns.',
            },
        ],
        stack: ['React', 'Node.js', 'native iOS', 'ShazamKit', 'content workflows', 'media synchronization', 'product analytics'],
        links: [
            {
                label: 'Feels Music Messaging',
                description: 'The current public product site.',
                url: 'https://www.feelsmusic.com/',
            },
            {
                label: 'App Store',
                description: 'The live iOS and iMessage product listing.',
                url: 'https://apps.apple.com/us/app/feels-music-messaging/id1641397528',
            },
            {
                label: 'Feels press page',
                description: 'Launch coverage and current company background.',
                url: 'https://www.feelsmusic.com/press',
            },
        ],
    },
    snap: {
        slug: 'snap',
        company: 'Snap Inc.',
        role: 'Camera Lead Design Engineer',
        dates: 'Apr 2020 to Sep 2022',
        logo: snapLogo,
        headline: 'Working prototypes that turned product ideas into measurable launches.',
        summary: 'I designed and built fully functional native prototypes across Camera, Creative Tools, and Memories, then partnered with production engineering through edge cases, launch, experimentation, and measurement.',
        metrics: [
            { value: '64M', label: 'daily users on Filter Carousel' },
            { value: '+38%', label: 'iOS filter impressions' },
            { value: '+10%', label: 'Android filter impressions' },
            { value: '3', label: 'design engineers led' },
        ],
        overview: [
            {
                title: 'The product work',
                body: 'I worked where interaction design met real platform behavior. Rather than stopping at static screens, I built realistic native prototypes so teams could experience timing, state, gestures, media behavior, and failure cases before investing in production code.',
            },
            {
                title: 'The systems underneath it',
                body: 'The work combined Objective-C, Swift, UIKit, product analytics, anonymized logs, controlled experiments, and user research. Production engineers wrote the shipping code; I stayed closely involved through implementation, launch, and measurement.',
            },
        ],
        media: [
            {
                image: snapFilterCarousel,
                alt: 'Preview Filter Carousel prototype and product case study',
                caption: 'Preview Filter Carousel: the working native prototype behind a launch used by 64 million people daily.',
            },
            {
                image: snapVoiceOver,
                alt: 'Voice Over tool case study and working prototype',
                caption: 'Voice Over: recording and editing audio over an existing Snap.',
            },
            {
                image: snapAutoCaption,
                alt: 'Auto-Caption tool case study and working prototype',
                caption: 'Auto-Caption: one-tap captions with movable, scalable, editable text.',
            },
        ],
        sections: [
            {
                number: '01',
                title: 'Preview Filter Carousel',
                body: 'I designed and built the working Objective-C prototype, then partnered with production engineering through launch. Experiments increased filter impressions by 38% on iOS and 10% on Android, and increased Snaps sent with filters by 9% and 5.4%, respectively.',
            },
            {
                number: '02',
                title: 'Creative tools in real code',
                body: 'I built functional prototypes for Voice Over, Auto-Caption, Text-to-Speech, Crop, Stickers, Lens Explorer, Lens Carousel, Universal Search, and editing flows. The prototypes made behavior testable in context before the production build began.',
            },
            {
                number: '03',
                title: 'Research, instrumentation, and leadership',
                body: 'I used analytics, anonymized logs, experiments, and UX research to improve creation, engagement, and discovery flows. I also led and mentored three design engineers while partnering with product, design, research, data science, and engineering on strategy and annual roadmaps.',
            },
        ],
        stack: ['Objective-C', 'Swift', 'UIKit', 'Figma', 'Origami', 'ProtoPie', 'product analytics', 'experimentation'],
        note: 'Role boundary: I designed and built the working prototypes. Production engineers wrote the shipping code, and I partnered with them through launch and measurement.',
        links: [
            {
                label: 'Snap Inc.',
                description: 'Snapchat and Snap\'s current product portfolio.',
                url: 'https://www.snap.com/',
            },
            {
                label: 'Snapchat Camera',
                description: 'Snap\'s product context for Camera and Creative Tools.',
                url: 'https://newsroom.snap.com/transparent-camera',
            },
            {
                label: 'Director Mode',
                description: 'A 2022 view of Snap\'s camera and editing tools.',
                url: 'https://newsroom.snap.com/sps2022creators',
            },
        ],
    },
    'story-star': {
        slug: 'story-star',
        company: 'Story Star',
        role: 'Co-Founder and Head of Mobile Development',
        dates: 'Dec 2015 to May 2020',
        logo: storyStarLogo,
        headline: 'A studio for taking consumer products from idea to users.',
        summary: 'I built and led development across more than 20 iOS and web products, spanning social networks, audiobooks, video editing, utilities, and games. The work combined product judgment, mobile engineering, backend services, growth, and monetization.',
        metrics: [
            { value: '20+', label: 'iOS and web products' },
            { value: '250K+', label: 'monthly users at the largest' },
            { value: '$20K+', label: 'best monthly revenue' },
            { value: 'Acquired', label: 'Spotlight by Snap' },
        ],
        overview: [
            {
                title: 'The product portfolio',
                body: 'Story Star was repeated zero-to-one product work. We explored consumer problems, built the product and its supporting services, learned from real usage, and either grew the result or moved on quickly.',
            },
            {
                title: 'The systems underneath it',
                body: 'The portfolio required native iOS engineering, web experiences, Firebase-backed services, media processing, social graphs, notifications, analytics, subscriptions, advertising, and the practical release work needed to keep many live products operating.',
            },
        ],
        media: [
            {
                image: storyStarSpotlight,
                alt: 'Four screens from the original Story Star Spotlight application',
                caption: 'The original Spotlight product: discovery, playback, notifications, and creator profiles.',
            },
        ],
        productsIntro: 'A public App Store archive preserves fifteen Story Star titles, while Spotlight is documented separately. Together they recover sixteen products from the broader portfolio.',
        products: [
            {
                name: 'Spotlight',
                category: 'Social video',
                description: 'A ranked feed for discovering, voting on, and submitting Snaps by topic. Snap acquired the product.',
                href: 'https://newsroom.snap.com/introducing-spotlight?lang=en-GB',
                sourceLabel: 'Snap',
                monogram: 'S',
                tone: 'spotlight',
                featured: true,
            },
            {
                name: 'Sponge',
                category: 'Audiobooks',
                description: 'A cloud audio player with variable speed and a public-domain catalog. The archived portfolio records 4,000 daily users and 70,000 books.',
                href: 'https://appstor.io/app/sponge-audiobook-mp3-player',
                sourceLabel: 'App Store archive',
                image: storyStarSponge,
                featured: true,
            },
            {
                name: 'Peek',
                category: 'Social video',
                description: 'Discover and follow Snapchat creators without adding every account to the main feed.',
                href: 'https://appstor.io/app/story-star-c1avft',
                sourceLabel: 'App Store archive',
                image: storyStarPeek,
            },
            {
                name: 'ellie',
                category: 'Social networking',
                description: 'Save, revisit, create, and share Snapchat Stories.',
                href: 'https://appstor.io/app/ellie-snapmemories',
                sourceLabel: 'App Store archive',
                image: storyStarEllie,
            },
            {
                name: 'Headswitch',
                category: 'Video editing',
                description: 'Place a chosen face into a video clip and share the result.',
                href: 'https://appstor.io/app/headswitch-put-any-head-any-vi',
                sourceLabel: 'App Store archive',
                image: storyStarHeadswitch,
            },
            {
                name: 'Switchcam',
                category: 'Camera effects',
                description: 'Face-tracked masks for transforming photos and videos.',
                href: 'https://appstor.io/app/switchcam-transform-yourself',
                sourceLabel: 'App Store archive',
                image: storyStarSwitchcam,
            },
            {
                name: 'Mustknow',
                category: 'Video Q&A',
                description: 'Anonymous video questions with face masking, voice distortion, and chat.',
                href: 'https://appstor.io/app/mustknow',
                sourceLabel: 'App Store archive',
                image: storyStarMustknow,
            },
            {
                name: 'Quvoke',
                category: 'Social Q&A',
                description: 'A social question-and-answer experiment built around provocative prompts.',
                href: 'https://appstor.io/app/quvoke-provocative-questions-a',
                sourceLabel: 'App Store archive',
                monogram: 'Q',
                tone: 'violet',
            },
            {
                name: 'Electric',
                category: 'Live video',
                description: 'A random video chat product for meeting people in seconds.',
                href: 'https://appstor.io/app/electric-video-chat-random-peo',
                sourceLabel: 'App Store archive',
                image: storyStarLogo,
            },
            {
                name: 'Fidget Spinner Live',
                category: 'Social video',
                description: 'A topical random-video product built around the 2017 fidget-spinner moment.',
                href: 'https://appstor.io/app/fidget-spinner-live-video-chat',
                sourceLabel: 'App Store archive',
                monogram: 'FS',
                tone: 'lime',
            },
            {
                name: 'Hydrate NOW',
                category: 'Health utility',
                description: 'A focused reminder utility for building a consistent water-drinking habit.',
                href: 'https://appstor.io/app/hydrate-now-drink-water-end-ha',
                sourceLabel: 'App Store archive',
                monogram: 'H',
                tone: 'blue',
            },
            {
                name: 'Chatspace',
                category: 'Live video',
                description: 'A fast, lightweight random video chat experience.',
                href: 'https://appstor.io/app/chatspace-random-video-chat',
                sourceLabel: 'App Store archive',
                monogram: 'C',
                tone: 'coral',
            },
            {
                name: 'Kings Cup',
                category: 'Party game',
                description: 'A digital deck with six rule sets, including a custom mode.',
                href: 'https://appstor.io/app/kings-cup-drinking-game-h04va0',
                sourceLabel: 'App Store archive',
                image: storyStarKingsCup,
            },
            {
                name: 'Advent Calendar 2017',
                category: 'Seasonal utility',
                description: 'A Christmas countdown revealing apps, music, books, and other surprises each day.',
                href: 'https://appstor.io/app/2017-christmas-advent-calendar',
                sourceLabel: 'App Store archive',
                image: storyStarAdventCalendar,
            },
            {
                name: 'OH MY GOD',
                category: 'Party game',
                description: 'A truth-or-dare game with five themed modes.',
                href: 'https://appstor.io/app/oh-my-god-truth-or-dare',
                sourceLabel: 'App Store archive',
                image: storyStarOhMyGod,
            },
            {
                name: 'Vibes',
                category: 'Snapchat memes',
                description: 'Anonymous meme sharing and comments designed around Snapchat Stories.',
                href: 'https://appstor.io/app/vibes-memes-snapchat',
                sourceLabel: 'App Store archive',
                image: storyStarVibes,
            },
        ],
        productsNote: 'This archive covers the products whose names or listings still survive publicly. Additional experiments from the 20+ product portfolio are not yet documented here.',
        sections: [
            {
                number: '01',
                title: 'Built a portfolio, not one narrow feature',
                body: 'I worked across product design, native mobile implementation, web, backend integration, release operations, analytics, and monetization. The products covered social video, audio, editing, utilities, and games, which forced the underlying patterns to become reusable.',
            },
            {
                number: '02',
                title: 'Spotlight from zero to acquisition',
                body: 'I built Spotlight as a social product for discovering and ranking entertaining Snaps using Swift, Firebase, and the Snap SDK. It supported topic discovery, voting, creator profiles, and direct submission from Snapchat. Snap acquired the product and made Spotlight a core Snapchat feature.',
            },
            {
                number: '03',
                title: 'Learned through scale and economics',
                body: 'The largest product reached more than 250,000 monthly active users, and the most profitable exceeded $20,000 per month. Building across many categories developed my habit of treating distribution, retention, reliability, and revenue as part of engineering, not as work that begins after launch.',
            },
        ],
        stack: ['Swift', 'native iOS', 'web applications', 'Firebase', 'Snap SDK', 'media products', 'analytics', 'subscriptions and ads'],
        links: [
            {
                label: 'Story Star App Store archive',
                description: 'Surviving listings for fifteen products published by Story Star Inc.',
                url: 'https://appstor.io/developers/1009437502-story-star-inc',
            },
            {
                label: 'Introducing Spotlight',
                description: 'Snap\'s official announcement of Spotlight in 2020.',
                url: 'https://newsroom.snap.com/introducing-spotlight?lang=en-GB',
            },
            {
                label: 'Spotlight at one year',
                description: 'Snap\'s retrospective on the product and creator ecosystem.',
                url: 'https://newsroom.snap.com/spotlight-1-year-anniversary',
            },
        ],
    },
    'rbc-wealth-management': {
        slug: 'rbc-wealth-management',
        company: 'RBC Wealth Management',
        role: 'Senior iOS Developer and Tech Lead',
        dates: 'Dec 2017 to Apr 2020',
        logo: rbcLogo,
        headline: 'Reliable financial software under bank-grade constraints.',
        summary: 'I led Objective-C and Swift development for wealth-management applications where availability, privacy, security, compliance, and clear communication were product requirements, not afterthoughts.',
        metrics: [
            { value: '25K+', label: 'daily active users' },
            { value: '$463B', label: 'assets under management served' },
            { value: '2', label: 'award-winning products' },
        ],
        overview: [
            {
                title: 'The products',
                body: 'The applications gave wealth-management clients secure access to information and communication that had to remain understandable and dependable across everyday use and high-stakes financial moments.',
            },
            {
                title: 'The systems underneath them',
                body: 'Native Objective-C and Swift clients sat inside bank-grade identity, security, privacy, compliance, availability, and release processes. Product quality depended as much on those constraints as on the interface itself.',
            },
        ],
        media: [
            {
                image: rbcMobileDashboard,
                alt: 'RBC Wealth Management mobile dashboard from the App Store',
                caption: 'The current public App Store presentation of RBC Wealth Management mobile access.',
                href: 'https://apps.apple.com/ca/app/rbc-wm-online-u-s/id1303918711',
                sourceLabel: 'View on the App Store',
            },
            {
                image: rbcMobileAccounts,
                alt: 'RBC Wealth Management mobile accounts screen from the App Store',
                caption: 'Secure portfolio and account access in the live public product listing.',
                href: 'https://apps.apple.com/ca/app/rbc-wm-online-u-s/id1303918711',
                sourceLabel: 'View on the App Store',
            },
        ],
        sections: [
            {
                number: '01',
                title: 'Led native client engineering',
                body: 'I led development of wealth-management applications serving more than 25,000 daily users and supporting a business with $463 billion in assets under management. The work covered hands-on implementation, technical direction, release quality, and coordination across dependent systems.',
            },
            {
                number: '02',
                title: 'Translated regulated workflows into software',
                body: 'I worked with product, design, security, compliance, and executives across RBC, City National Bank, and IBM to turn financial requirements into reliable mobile experiences without weakening the controls around them.',
            },
            {
                number: '03',
                title: 'Brought critical work in-house',
                body: 'I helped assess an outsourced engagement and transfer development of the City National Bank application in-house. Both shipped products later won Best Private Bank for Digital Client Communication in North America at the inaugural Global Wealth Tech Awards.',
            },
        ],
        stack: ['Objective-C', 'Swift', 'UIKit', 'secure mobile architecture', 'regulated workflows', 'accessibility', 'release quality'],
        links: [
            {
                label: 'RBC WM Online',
                description: 'The current iOS product listing.',
                url: 'https://apps.apple.com/ca/app/rbc-wm-online-u-s/id1303918711',
            },
            {
                label: 'Awards and recognition',
                description: 'RBC\'s record of its digital client communication award.',
                url: 'https://docs.rbcwealthmanagement.com/us/011100-awards-and-recognition.pdf',
            },
            {
                label: 'RBC Wealth Management',
                description: 'The current wealth management product and service context.',
                url: 'https://www.rbcwealthmanagement.com/en-us/',
            },
        ],
    },
    'kinetic-commerce': {
        slug: 'kinetic-commerce',
        company: 'Kinetic Commerce',
        role: 'Senior iOS Developer',
        dates: 'Sep 2017 to Dec 2017',
        logo: kineticCommerceLogo,
        logoFit: 'contain',
        headline: 'Native software for connected retail, from the sales floor to the Mac.',
        summary: 'I built iPad and macOS software for in-store retail experiences at a Toronto design and technology firm connecting digital commerce with the physical store.',
        metrics: [
            { value: 'iPad + Mac', label: 'native product surfaces' },
            { value: 'In-store', label: 'retail environment' },
            { value: '2017', label: 'senior engineering role' },
        ],
        overview: [
            {
                title: 'The product context',
                body: 'Kinetic Commerce built connected-retail products for customer and store-associate experiences. My work focused on native software used on iPad and Mac inside that broader retail platform.',
            },
            {
                title: 'The systems underneath it',
                body: 'In-store software has to fit the device, physical environment, staff workflow, customer journey, and existing commerce systems at the same time. That made the work a practical lesson in designing across software and place.',
            },
        ],
        media: [
            {
                image: kineticCommerceCover,
                alt: 'Kinetic Commerce connected retail company banner',
                caption: 'Kinetic Commerce positioned its platform around connected customer and associate retail experiences.',
                href: 'https://ca.linkedin.com/company/kineticcommerce',
                sourceLabel: 'Company profile on LinkedIn',
            },
        ],
        sections: [
            {
                number: '01',
                title: 'Built for iPad and macOS',
                body: 'I contributed hands-on native engineering across iPad and Mac applications built for the store environment, where clarity, speed, and dependable behavior mattered in live customer interactions.',
            },
            {
                number: '02',
                title: 'Worked across physical and digital retail',
                body: 'The role strengthened my understanding that a retail experience is never only a screen. Device constraints, associate workflows, customer expectations, and commerce infrastructure all shape the product.',
            },
            {
                number: '03',
                title: 'Delivered in a focused senior role',
                body: 'I joined as a senior iOS developer for a concentrated engagement and contributed to the native product layer of Kinetic\'s connected-retail platform.',
            },
        ],
        stack: ['native iOS', 'macOS', 'iPad experiences', 'connected retail', 'multi-device UX', 'commerce workflows'],
        note: 'Kinetic Commerce\'s former domain now redirects to an unrelated site, so this page links only to preserved company and editorial records.',
        links: [
            {
                label: 'Kinetic Commerce profile',
                description: 'Preserved company background and connected-retail product overview.',
                url: 'https://ca.linkedin.com/company/kineticcommerce',
            },
            {
                label: 'ALDO platform launch',
                description: 'Contemporary coverage of Kinetic\'s connected-commerce platform.',
                url: 'https://betakit.com/toronto-based-kinetic-cafe-launches-ecommerce-platform-at-aldos-world-trade-centre-location/',
            },
            {
                label: '2017 company profile',
                description: 'A period profile of Kinetic\'s retail technology work.',
                url: 'https://ctaconnects.com/2017/04/retail-and-tech-stacking-up-to-make-it-big/',
            },
        ],
    },
    'turbo-rocket-games': {
        slug: 'turbo-rocket-games',
        company: 'Turbo Rocket Games',
        role: 'Gameplay Programmer',
        dates: '2016',
        logo: turboRocketGamesLogo,
        domain: 'turborocketgames.com',
        headline: 'Live multiplayer gameplay inside a large 3D world.',
        summary: 'I wrote ShiVa and Lua gameplay systems for Dragon Sim Online, including NPCs, quest progression, backend integration, and AdMob monetization for a live multiplayer game with hundreds of concurrent players.',
        metrics: [
            { value: 'Global', label: 'Google AdMob challenge winner' },
            { value: '100+', label: 'student teams in the challenge' },
            { value: '500K+', label: 'downloads when Google announced the win' },
            { value: '4.5★', label: 'average rating at the time' },
        ],
        overview: [
            {
                title: 'The game',
                body: 'Dragon Sim Online lets players raise and customize dragons, explore a 3D fantasy world, fight enemies, complete progression loops, and play with people around the world.',
            },
            {
                title: 'The systems underneath it',
                body: 'The experience combined gameplay code, multiplayer state, NPC behavior, quest progression, backend services, and monetization. Each system had to support a world that kept moving when more players joined.',
            },
        ],
        media: [
            {
                image: dragonSimWorld,
                alt: 'Dragon Sim Online multiplayer gameplay in a 3D world',
                caption: 'Dragon Sim Online: multiplayer exploration and combat in the live 3D world.',
                href: 'https://apps.apple.com/us/app/dragon-sim-online/id1128156386',
                sourceLabel: 'View on the App Store',
            },
            {
                image: dragonSimGameplay,
                alt: 'Dragon Sim Online gameplay and quest environment',
                caption: 'The public game listing shows the progression, world, and simulation systems working together.',
                href: 'https://apps.apple.com/us/app/dragon-sim-online/id1128156386',
                sourceLabel: 'View on the App Store',
            },
        ],
        sections: [
            {
                number: '01',
                title: 'Implemented gameplay systems',
                body: 'I wrote ShiVa and Lua code for gameplay behavior, NPCs, and quest progression. The work connected moment-to-moment play with the longer progression loops that kept the simulation engaging.',
            },
            {
                number: '02',
                title: 'Built for a live multiplayer world',
                body: 'The title supported hundreds of concurrent players. That experience taught me that social and multiplayer mechanics must improve the core product as more people join, not simply place social features around it.',
            },
            {
                number: '03',
                title: 'Won on both product and execution',
                body: 'Turbo Rocket Games won the 2016 Google AdMob Student App Challenge from more than 100 teams worldwide. Google highlighted the team\'s division of responsibilities, the game\'s product depth, and its early traction.',
            },
        ],
        stack: ['ShiVa', 'Lua', '3D gameplay', 'multiplayer systems', 'NPCs', 'quest progression', 'backend integration', 'AdMob'],
        note: 'The 500K+ download and 4.5-star figures are the numbers Google reported when announcing the 2016 winner. The game remains live today.',
        links: [
            {
                label: 'Google winner announcement',
                description: 'Google\'s official 2016 AdMob Student App Challenge result.',
                url: 'https://blog.google/products/admob/announcing-winning-team-for-2016-admob-student-app-challenge/',
            },
            {
                label: 'Dragon Sim on the App Store',
                description: 'The live iOS game and its public screenshots.',
                url: 'https://apps.apple.com/us/app/dragon-sim-online/id1128156386',
            },
            {
                label: 'Dragon Sim on Google Play',
                description: 'The live Android game listing.',
                url: 'https://play.google.com/store/apps/details?id=com.turborocketgames.dragonsim',
            },
            {
                label: 'Turbo Rocket Games',
                description: 'The studio\'s current game catalogue.',
                url: 'https://turborocketgames.com/games/',
            },
        ],
    },
    eventlime: {
        slug: 'eventlime',
        company: 'Eventlime',
        role: 'Co-Founder',
        dates: '2015 to 2017',
        logoText: 'EL',
        headline: 'An early co-founder chapter in zero-to-one product building.',
        summary: 'I co-founded Eventlime while studying at Brock, gaining early experience shaping a product and navigating the practical decisions behind a new software venture.',
        metrics: [
            { value: '2 yrs', label: 'co-founder tenure' },
            { value: '0 to 1', label: 'product stage' },
            { value: 'Founder', label: 'product and delivery context' },
        ],
        overview: [
            {
                title: 'The venture',
                body: 'Eventlime was an early-stage product venture built during graduate school. It gave me a direct view of how product definition, software delivery, and company-building decisions interact.',
            },
            {
                title: 'What carried forward',
                body: 'The experience became part of the foundation for later co-founder work at Story Star and Santa\'s AI Lab: stay close to the product, learn from real use, and treat distribution and execution as engineering concerns.',
            },
        ],
        sections: [
            {
                number: '01',
                title: 'Worked from an early product idea',
                body: 'As a co-founder, I helped move the venture from an idea toward a working product while balancing the ambiguity, prioritization, and limited resources that define early-stage work.',
            },
            {
                number: '02',
                title: 'Learned the founder operating loop',
                body: 'The role developed an early habit of connecting product choices with delivery, feedback, and what a small team could realistically support.',
            },
            {
                number: '03',
                title: 'Built a base for later ventures',
                body: 'Eventlime preceded the broader consumer portfolio at Story Star and the deeper platform work at Santa\'s AI Lab, where those early lessons were applied at much greater scale.',
            },
        ],
        stack: ['zero-to-one product development', 'product discovery', 'founder operations', 'early-stage delivery'],
        note: 'No reliable public product demo from this period remains online, so this page intentionally avoids reconstructing features that cannot be verified.',
        links: [
            {
                label: 'Career history on LinkedIn',
                description: 'My public professional profile and career timeline.',
                url: 'https://www.linkedin.com/in/ibakurov',
            },
            {
                label: 'MaRS Entrepreneurship 101',
                description: 'The entrepreneurship program I completed during this period.',
                url: 'https://www.credential.net/10460359',
            },
        ],
    },
    'brock-university': {
        slug: 'brock-university',
        company: 'Brock University',
        role: 'Graduate Teaching Assistant',
        dates: '2015 to 2017',
        logo: brockLogo,
        logoFit: 'contain',
        domain: 'brocku.ca',
        headline: 'Teaching computer science and product design by making hard ideas usable.',
        summary: 'I supported students across Python, algorithms, computer networking, and product design while completing my MSc. I also delivered lectures based on Don Norman\'s The Design of Everyday Things, connecting design principles to products people could understand and use.',
        metrics: [
            { value: '2 yrs', label: 'teaching alongside graduate study' },
            { value: 'Python', label: 'programming instruction' },
            { value: 'Networks', label: 'computer networking course' },
            { value: 'Product', label: 'design lectures and critique' },
        ],
        overview: [
            {
                title: 'The teaching work',
                body: 'I held office hours and helped students reason through Python, algorithms, computer networking, assignments, and debugging. The goal was not to hand over an answer, but to help someone build a mental model they could reuse.',
            },
            {
                title: 'Product design in the classroom',
                body: 'For a product design course, I delivered lectures grounded in Don Norman\'s The Design of Everyday Things. We used its ideas about affordances, feedback, mapping, constraints, and human error to examine why products feel intuitive or confusing.',
            },
        ],
        sections: [
            {
                number: '01',
                title: 'Made programming and algorithms concrete',
                body: 'I helped students break problems into smaller steps, trace program behavior, and connect algorithmic ideas to the code in front of them.',
            },
            {
                number: '02',
                title: 'Taught product design through a classic text',
                body: 'I prepared and delivered lectures on The Design of Everyday Things, translating its principles into practical ways to evaluate interfaces, physical products, and the relationship between a system and the person using it.',
            },
            {
                number: '03',
                title: 'Connected networking concepts to real systems',
                body: 'As a teaching assistant for a computer networking course, I helped students reason about how data moves between systems and how protocols, layers, and implementation decisions shape reliable communication.',
            },
        ],
        stack: ['Python', 'algorithms', 'computer networking', 'product design', 'The Design of Everyday Things', 'lecturing', 'debugging', 'office hours', 'technical communication'],
        links: [
            {
                label: 'Brock Computer Science',
                description: 'The university\'s current computer science program.',
                url: 'https://brocku.ca/programs/undergraduate/computer-science/',
            },
            {
                label: 'MSc thesis',
                description: 'My graduate research in Brock\'s repository.',
                url: 'https://brocku.scholaris.ca/items/eef68d48-0a0b-4c63-a427-54e233e77ce6',
            },
            {
                label: 'Springer publication',
                description: 'The published research that followed the thesis work.',
                url: 'https://link.springer.com/chapter/10.1007/978-3-319-77583-8_3',
            },
        ],
    },
    'agency-sm-art': {
        slug: 'agency-sm-art',
        company: 'Agency SM.Art',
        role: 'iOS Developer',
        dates: '2014 to 2016',
        logoText: 'SM',
        headline: 'Early native mobile work in banking and insurance.',
        summary: 'I built native iOS applications for banking and insurance clients in Kyiv, learning how to turn structured, high-trust workflows into clear mobile experiences.',
        metrics: [
            { value: '2 yrs', label: 'native iOS development' },
            { value: '2', label: 'regulated client domains' },
            { value: 'Kyiv', label: 'agency engineering role' },
        ],
        overview: [
            {
                title: 'The client work',
                body: 'The role covered iOS delivery for banking and insurance applications. It was my first sustained professional experience building mobile software around sensitive information and structured customer workflows.',
            },
            {
                title: 'The foundation it created',
                body: 'Working on client products built the habits that later mattered at RBC Wealth Management: careful state handling, clear information hierarchy, dependable releases, and respect for trust-sensitive domains.',
            },
        ],
        sections: [
            {
                number: '01',
                title: 'Built native client applications',
                body: 'I implemented user-facing iOS experiences for banking and insurance products, working within the requirements and delivery rhythms of an agency environment.',
            },
            {
                number: '02',
                title: 'Learned trust-sensitive product work',
                body: 'Financial and insurance interfaces made correctness and clarity part of the product itself. A confusing state or unreliable flow could undermine the user\'s confidence in the whole service.',
            },
            {
                number: '03',
                title: 'Established a mobile engineering base',
                body: 'This role began the native mobile path that continued through Kinetic Commerce, RBC Wealth Management, Story Star, Snap, Feels, and later Apple-platform work at Santa\'s AI Lab.',
            },
        ],
        stack: ['native iOS', 'mobile UI', 'banking workflows', 'insurance applications', 'client delivery', 'release quality'],
        note: 'The applications were delivered through the agency for clients and are not publicly attributed, so no client names or unverified product screenshots are included.',
        links: [
            {
                label: 'Career history on LinkedIn',
                description: 'My public professional profile and career timeline.',
                url: 'https://www.linkedin.com/in/ibakurov',
            },
        ],
    },
};
